/**
 * This file is a utility file for the functionality of the Vendor Application form.
 */

import {
  chain,
  every,
  get,
  isEmpty,
  keys,
  omit,
  pick,
  some,
  values,
} from 'lodash';
import * as valid from 'us-bank-account-validator';
import NSApplicationStatus from '../../Constants/ns-vendor-application-status';
import ApplicationStatus from '../../Constants/vendor-application-status';
import VendorNSUpdateFields from '../../Constants/ns-vendor-update-fields';
import { makeBankValues } from './bank-fields';
import validateRouting4LastDigits from './routing-validation';
/**
 * creates an object of all of the required fields on the Vendor Application
 *
 * @param {object} form the fields and values from the form
 * @param {boolean} wirePayment if the form has the wire payment section filled
 * @param {boolean} update if the form is an update to an existing vendor
 *
 * @returns {object} the application fields that are required for the scenario
 */
export function findRequiredFields(form, wirePayment, update) {
  if (update) return pick(form, keys(VendorNSUpdateFields));

  let fieldsToCheck = omit(form, [
    'invited_by',
    'invited_by_user',
    'invite_token_id',
    'address_2',
    'payment_billing_address_2',
    'submitted_at',
    'portal_message',
    'default_delivery_location',
    'application_status',
    'application_archived_at',
    'fcr_expiration',
    'grade',
    'grade_updated_at',
    'attributes',
    'approved_at',
    'approved_by',
    'ssn',
    'is_individual',
    'influencer_handle',
    'influencer_platform',
    'influencer_handles',
    'w8_form',
    'invite_token',
    'approval_signatures',
    'approval_signed_by',
    'middle_name',
    'vendor_documents',
    'custom_payment_terms',
    'payment_term',
    'is_direct',
    'coi_not_available',
    'divisions',
    'sponsor_name',
    'sponsor_id',
    'merch_type',
    'manufacturing_type',
    'existing_vendor',
    'requested_item_lead_times_count',
  ]);

  if (!wirePayment) {
    fieldsToCheck = omit(fieldsToCheck, [
      'wire_account_name',
      'wire_account_number',
      'wire_bank_name',
      'wire_routing_number',
    ]);
  }

  if (form.country !== 'US') {
    fieldsToCheck = omit(fieldsToCheck, ['state', 'zip']);
  }

  if (form.payment_country !== 'US') {
    fieldsToCheck = omit(fieldsToCheck, ['payment_state', 'payment_zip']);
  }

  if (form.coi_not_available) {
    fieldsToCheck = omit(fieldsToCheck, [
      'certificate_of_insurance',
      'coi_expiration_date',
    ]);
  }

  if (form.country !== 'US') {
    fieldsToCheck = omit(fieldsToCheck, [
      'w9_form',
      'tax_id',
      'tax_classification',
      'coi_not_available',
    ]);
    if (form.is_individual) {
      fieldsToCheck = omit(fieldsToCheck, ['w8bene_form']);
    } else {
      fieldsToCheck = omit(fieldsToCheck, ['w8ben_form']);
    }
  } else {
    fieldsToCheck = omit(fieldsToCheck, ['w8ben_form', 'w8bene_form']);
  }

  if (form.is_individual) {
    fieldsToCheck = omit(fieldsToCheck, ['legal_name', 'company_name']);
  } else {
    fieldsToCheck = omit(fieldsToCheck, ['first_name', 'last_name']);
  }

  if (form.beauty_vendor_agreement) {
    fieldsToCheck = omit(fieldsToCheck, ['vendor_agreement']);
  } else {
    fieldsToCheck = omit(fieldsToCheck, ['beauty_vendor_agreement']);
  }

  return fieldsToCheck;
}

/**
 * Gets the status of the Vendor Application based on the Vendor object passed in
 * @param {object} vendor the vendor returned from the database
 *
 * @returns {string} the status of the application
 */
export function getApplicationStatus(vendor) {
  const status = get(vendor, 'application_status');
  if (
    vendor.approved_at &&
    vendor.submitted_at &&
    status !== NSApplicationStatus.APPROVED
  ) {
    return ApplicationStatus.PENDING_ACCOUNTING_APPROVAL;
  }

  if (
    (vendor.approved_at && !vendor.internal_id) ||
    (vendor.submitted_at && !vendor.internal_id) ||
    (vendor.submitted_at && vendor.rejection_reason) ||
    status === NSApplicationStatus.PENDING_APPROVAL ||
    status === NSApplicationStatus.PENDING_RE_APPROVAL
  ) {
    return ApplicationStatus.PENDING_APPROVAL;
  }
  if (vendor.rejection_reason) {
    return ApplicationStatus.REJECTED;
  }
  if (vendor.internal_id) {
    return ApplicationStatus.APPROVED;
  }
  return ApplicationStatus.APPLICATION_PENDING;
}

/**
 * Determines if field should be disabled based on the application status
 * @param {string} status the status of the Vendor Application
 *
 * @returns {boolean} whether field is disabled
 */
export function isFieldDisabled(status) {
  if (status === ApplicationStatus.APPROVED) return true;
  if (status === ApplicationStatus.PENDING_APPROVAL) return true;
  if (status === ApplicationStatus.PENDING_ACCOUNTING_APPROVAL) return true;
  if (status === ApplicationStatus.ARCHIVE) return true;
  if (status === ApplicationStatus.REJECTED) return false;
  return false;
}

/**
 * Determines if field should be disabled based on the application status
 * @param {string} status the status of the Vendor Application
 *
 * @returns {boolean} whether field is disabled
 */
export function isFieldBankingRequestEnabled(status) {
  if (status === ApplicationStatus.APPROVED) return true;
  if (status === ApplicationStatus.PENDING_APPROVAL) return false;
  if (status === ApplicationStatus.PENDING_ACCOUNTING_APPROVAL) return false;
  if (status === ApplicationStatus.REJECTED) return true;
  return false;
}

/**
 * Determines if file field should be disabled based on the application status
 * @param {string} status the status of the Vendor Application
 *
 * @returns {boolean} whether field is disabled
 */
export function isFileFieldDisabled(status) {
  if (status === ApplicationStatus.PENDING_APPROVAL) return true;
  if (status === ApplicationStatus.PENDING_ACCOUNTING_APPROVAL) return true;
  return false;
}

/**
 * Checks for valid Bank Account Number
 * @param {string} num the bank account number
 * @param {boolean} domestic whether the vendor is domestic
 *
 * @returns {boolean} whether the bank account number is valid
 */
export function isValidAccountNumber(num, domestic = true) {
  if (isEmpty(num)) return true;

  return domestic
    ? valid.accountNumber(num).isValid
    : Boolean(num.match(/^[A-Za-z0-9*]{0,34}$/));
}

/**
 * Checks for valid Routing Number
 * @param {string} num the routing number
 * @param {boolean} domestic whether the vendor is domestic
 *
 * @returns {boolean} whether the routing number is valid
 */
export function isValidRoutingNumber(num, domestic = true) {
  if (isEmpty(num)) return true;

  const domesticValidation =
    num[0] >= '0' && num[0] <= '9'
      ? valid.routingNumber(num).isValid
      : validateRouting4LastDigits(num);
  return domestic
    ? domesticValidation ||
        num === '084009519' ||
        num === '124303214' ||
        num === '091311229' ||
        num === '124303243' ||
        num === '031101334' ||
        num === '071919133'
    : Boolean(num.match(/^[A-Za-z0-9*]{0,11}$/));
}

/**
 * Checks for empty platform in influencer_handles
 * @param {array} influencerHandles
 *
 * @returns {boolean} whether influencer_handles contains an empty platform
 */
export function influencerHandlesHasEmptyPlatform(influencerHandles) {
  if (!influencerHandles || influencerHandles.length === 0) return false;
  return influencerHandles.some((handle) => !handle.social_media_platform);
}

/**
 * Checks for empty handle in influencer_handles
 * @param {array} influencerHandles
 *
 * @returns {boolean} whether influencer_handles contains an empty handle
 */
export function influencerHandlesHasEmptyHandle(influencerHandles) {
  if (!influencerHandles || influencerHandles.length === 0) return false;
  return influencerHandles.some((handle) => !handle.social_media_handle);
}

/**
 * Determines if Submit Button should be shown on Vendor Application
 * @param {string} internal_id the vendor's internal id if it has one
 * @param {string} rejection_reason the vendor's rejection reason if it has one
 *
 * @returns {}
 */
export function showSubmitButton(
  internal_id,
  rejection_reason,
  vendorFromNewVendorOnboarding,
) {
  if (rejection_reason) return true;
  if (vendorFromNewVendorOnboarding) return true;
  if (internal_id) return false;
  return true;
}

export function makeSubmitButtonTextWithSignedFiles(status, taxFormCompleted) {
  if (status === ApplicationStatus.APPROVED) {
    return 'submit application';
  }
  if (!taxFormCompleted) {
    return `continue to sign tax documents`;
  }
  if (status === ApplicationStatus.PENDING_APPROVAL) {
    return 'application submitted';
  }
  if (status === ApplicationStatus.REJECTED) {
    return 'resubmit application';
  }
  return 'submit application';
}

/**
 * Create label for submit button based on vendor application status
 * @param {string} status the status of the vendor application
 *
 * @returns {string} the submit button text
 */
export function makeSubmitButtonText(status) {
  if (status === ApplicationStatus.PENDING_APPROVAL) {
    return 'application submitted';
  }
  if (status === ApplicationStatus.REJECTED) {
    return 'resubmit application';
  }
  return 'submit application';
}

/**
 * Checks if all required fields for the form are filled on submit
 * for creating a vendor in NetSuite
 * @param {object} form all of the fields and values from the form
 * @param {boolean} hasWirePayment whether the wire payment fields were enabled
 *
 * @returns {boolean} whether all required fields were filled out
 */
export function validateFormForCreate(form, hasWirePayment = false) {
  const requiredFields = findRequiredFields(form, hasWirePayment);
  return every(values(requiredFields));
}

/**
 * Checks if all required fields for the form are filled on submit
 * for updating a vendor in NetSuite
 * @param {object} form all of the fields and values from the form
 *
 * @returns {boolean} whether all required fields were filled out
 */
export function validateFormForUpdate(form) {
  const fields = pick(form, keys(VendorNSUpdateFields));
  return every(values(fields));
}

/**
 * Validates the phone number to have
 * at least 7 digits and no letters.
 *
 * @param phone {string}
 * @param missing {boolean} // ability to override check if true;
 * @returns {boolean}
 */
export function isValidPhone(phone, missing = false) {
  if (missing) return true;
  if (isEmpty(phone)) return false;
  const strip = /[|&;$%@"<>()+\-,\s]/gi;
  const match = /^\d{7,}$/;
  return !phone.replace(strip, '').match(match);
}

/**
 * Validates email format
 * @param {string} email the email to validate
 * @param {boolean} missing whether to allow empty emails (for optional fields)
 * @returns {boolean} returns true if the email is invalid, false otherwise
 */
export function isValidEmail(email, missing = false) {
  if (missing) return true;
  if (isEmpty(email)) return false;
  const validEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return !validEmail.test(email);
}

/**
 * Creates an array of the missing fields from the submitted form
 * @param {object} form all of the fields and values from submitted form
 * @param {boolean} hasWirePayment whether the wire payment section of form is enabled
 * @param {boolean} update whether the submit is to update an existing vendor
 *
 * @returns {array} an array of any required fieldst that are missing from the form
 */
export function findMissingFields(
  form,
  hasWirePayment = false,
  update = false,
) {
  const requiredFields = findRequiredFields(form, hasWirePayment, update);
  return chain(requiredFields).omitBy().keys().value();
}

export function buildContactEmail({
  first_name,
  last_name,
  email,
  vendor_name,
  id,
  role,
}) {
  const mailto = `${role === 'INFLUENCER' ? 'influencerdocs@fashionnova.com' : 'vendormasterdata@fashionnova.com'}`;
  const subject = `Connect Vendor Request for ${vendor_name}`;
  const body = `To whom it may concern:%0d%0dI would like to request this account to be connected to the ${vendor_name}.%0d%0d User Id: ${id}%0dUser Email: ${email}%0dCompany Name: ${vendor_name}%0d%0dThank you,%0d%0d${first_name} ${last_name}`;
  return `mailto:${mailto}?subject=${subject}&body=${body}`;
}

/**
 * Tests whether the wire button should be shown or not.
 * @param {boolean} domesticPayment is a domestic payment?
 * @param {string} status status of the vendor application
 *orm
 * @returns {boolean}
 * */
export function showWireButton(domesticPayment, status) {
  const isSubmitted =
    status === ApplicationStatus.APPROVED ||
    status === ApplicationStatus.PENDING_APPROVAL;
  if (isSubmitted) return false;
  return !isSubmitted && domesticPayment;
}

/**
 * Tests whether the wire fields should be shown or now
 * @param {object} form form data from the vendor record
 * @param {boolean} wirePayment the state of the button to enable display of
 * fields
 * @param {string} status status of the vendor application
 *
 * @returns {boolean}
 */
export function showWireFields(form, wirePayment, status) {
  if (wirePayment) return true;
  const statuses = [
    ApplicationStatus.APPROVED,
    ApplicationStatus.PENDING_APPROVAL,
  ];
  const isSubmitted = some(statuses, (s) => s === status);
  return isSubmitted && some(makeBankValues(form, true));
}
